import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import './AdventurePage.css';
import { getOfferings } from '../../api/offerings';
import { getToken, getUserId } from '../../api/index';
import SEO from '../../Components/SEO/SEO';
import Footer from '../../Components/Donation/Footer/Footer';

const ORG_BENEFITS = [
  { title: 'Flexible Setup', desc: 'List services with customizable quotes or events with ticket booking options easily.' },
  { title: 'Manage Bookings & Quotes', desc: 'Track inquiries, send custom quotes, and confirm bookings live from your dashboard.' },
  { title: 'Secure Stripe Payouts', desc: 'Accept payments via card or digital wallet, with payouts deposited directly via Stripe.' },
];

/* ── Navbar ─────────────────────────────── */
function Navbar({ navigate }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  return (
    <>
      <nav className={`an ${scrolled ? 'an--scrolled' : ''}`}>
        <button className="an__logo" onClick={() => navigate('/')}>
          <img
            className="an__logo-img"
            src="/Assets/Donation/DonationHeader/amalgamateLogoo.svg"
            alt="Amalgamate Logo"
          />
        </button>

        <div className="an__right">
          <button className="an__ghost" onClick={() => navigate('/login')}>Sign In</button>
          <button className="an__back-hub" onClick={() => navigate('/')}>← Back to Hub</button>
          <button className="an__hamburger" onClick={() => setMobileOpen(true)}>☰</button>
        </div>
      </nav>

      {mobileOpen && <div className="an__overlay" onClick={() => setMobileOpen(false)} />}
      <div className={`an__drawer ${mobileOpen ? 'an__drawer--open' : ''}`}>
        <button className="an__drawer-close" onClick={() => setMobileOpen(false)}>✕</button>
        <div className="an__logo" style={{ marginBottom: 32, background: 'none', border: 'none' }}>
          <img
            className="an__logo-img"
            src="/Assets/Donation/DonationHeader/amalgamateLogoo.svg"
            alt="Amalgamate Logo"
          />
        </div>
        <div className="an__drawer-actions">
          <button className="an__back-hub" style={{ width: '100%', marginBottom: 12 }} onClick={() => { setMobileOpen(false); navigate('/'); }}>← Back to Hub</button>
          <button className="an__ghost" style={{ width: '100%', marginBottom: 12 }} onClick={() => { setMobileOpen(false); navigate('/login'); }}>Sign In</button>
        </div>
      </div>
    </>
  );
}

/* ── Hero ───────────────────────────────── */
function Hero() {
  return (
    <section className="ah">
      <img src="/Assets/AmalgamateHUb/facilitate_bg.jpg" alt="Facilitate background" className="ah__bg" />
      <div className="ah__overlay" />
      <div className="ah__content">
        <div className="ah__eyebrow">⚡ ACTION & TRANSACTION HUB</div>
        <h1 className="ah__title">
          Facilitate &ndash;<br />
          <span className="c-green">Transactions that empower Change</span>
        </h1>
        <div className="ah__desc">
          <p className="ah__sub">
            Amalgamate is designed to facilitate the real world changes that people want. We currently do that through taxation. Similarly, Amalgamate will facilitate transactions for you and your business and use a percentage of those transactions to pay for the services and causes that people deem the most necessary. Most of your transaction will go directly back to you. A small portion of your sale will go in your personal voting account for you to vote with and a portion will go to Amalgamate for us to operate, grow and eventually facilitate true democracy, collective decision making and self governance!
          </p>
          <p className="ah__sub">
            Participate by listing your goods and services for sale. Create events, sell tickets and bring your business to Amalgamate. Don't spend money on marketing. Make money by bringing other users to Amalgamate and together we will actually solve the problems that taxation hasn't.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ── Featured Events ────────────────────── */
function Featured({
  offerings,
  isLoading,
  onBook,
  searchQuery,
  setSearchQuery,
  navigate
}) {
  const [filter, setFilter] = useState('all');

  const formatPrice = (offering) => {
    if (!offering.pricingModel) return offering.price || 'Free';
    const { pricingModel, price, currency } = offering;
    if (pricingModel === 'free') return 'Free';
    if (pricingModel === 'cod') return 'Cash on Delivery';

    let cleanCurrency = (currency || 'USD').trim().toUpperCase();
    if (cleanCurrency === 'RS') cleanCurrency = 'INR';
    if (!/^[A-Z]{3}$/.test(cleanCurrency)) cleanCurrency = 'USD';

    let formatted;
    try {
      formatted = new Intl.NumberFormat(undefined, {
        style: 'currency', currency: cleanCurrency
      }).format(price || 0);
    } catch (e) {
      formatted = `${cleanCurrency} ${(price || 0).toFixed(2)}`;
    }

    const modelSuffix = pricingModel === 'per_hour' ? '/hr' : pricingModel === 'per_day' ? '/day' : '';
    return `${formatted}${modelSuffix}`;
  };

  const getGradient = (id) => {
    const gradients = [
      'linear-gradient(160deg,#0d3028,#0a4a35)',
      'linear-gradient(160deg,#12102e,#0d1550)',
      'linear-gradient(160deg,#2e1010,#4a1500)',
      'linear-gradient(160deg,#252510,#3a2e08)',
      'linear-gradient(160deg,#102028,#0d2040)',
      'linear-gradient(160deg,#0d2a10,#0a3a18)'
    ];
    return gradients[id % gradients.length];
  };

  // Compute Quality Score for ranking
  const computeQualityScore = (item) => {
    let score = 0;
    // Real cover image (high visual value for landing page)
    if (item.coverImageUrl && item.coverImageUrl.trim() !== '') score += 50;
    // Verified provider name
    if (item.provider && item.provider.name) score += 20;
    // Defined price & pricing model
    if (item.pricingModel && item.pricingModel !== '') score += 10;
    if (item.price && Number(item.price) > 0) score += 10;
    // Active stock status
    if (item.status === 'active' || item.stockStatus === 'ok') score += 10;
    // Delivery info or location
    if (item.city || item.deliveryType) score += 5;
    return score;
  };

  // Rank and select top 6 offerings with category diversity
  const topOfferings = useMemo(() => {
    const list = offerings || [];
    if (list.length === 0) return [];

    // Filter by category if specific filter chosen
    const categoryFiltered = filter === 'all'
      ? list
      : list.filter(o => o.category === filter);

    // Sort by Quality Score descending, with recency as tie-breaker
    const sorted = [...categoryFiltered].sort((a, b) => {
      const scoreA = computeQualityScore(a);
      const scoreB = computeQualityScore(b);
      if (scoreB !== scoreA) return scoreB - scoreA;
      return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
    });

    if (filter !== 'all') {
      return sorted.slice(0, 6);
    }

    // Diverse mix when "All" is active: up to 2 services, 2 events, 2 products
    const services = sorted.filter(o => o.category === 'service');
    const events = sorted.filter(o => o.category === 'event');
    const products = sorted.filter(o => o.category === 'product');

    const selected = [];
    const maxPerCat = 2;

    // Pick top from each category
    selected.push(...services.slice(0, maxPerCat));
    selected.push(...events.slice(0, maxPerCat));
    selected.push(...products.slice(0, maxPerCat));

    // Fill remaining up to 6 with highest quality items not yet included
    if (selected.length < 6) {
      const selectedIds = new Set(selected.map(s => s.offeringId));
      for (const item of sorted) {
        if (!selectedIds.has(item.offeringId)) {
          selected.push(item);
          selectedIds.add(item.offeringId);
          if (selected.length >= 6) break;
        }
      }
    }

    // Final sort of selected 6 items by score
    return selected.sort((a, b) => {
      const scoreA = computeQualityScore(a);
      const scoreB = computeQualityScore(b);
      if (scoreB !== scoreA) return scoreB - scoreA;
      return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
    });
  }, [offerings, filter]);

  return (
    <section className="adv-sec adv-featured">
      <div className="adv-sec__hdr">
        <h2 className="adv-sec__title">User created <span className="c-green">Services and events</span></h2>
        <p className="adv-sec__sub">Life is happening in Amalgamate. Join the adventure!</p>
      </div>

      <div className="adv-filter-pills">
        <button className={`adv-filter-pill ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>All</button>
        <button className={`adv-filter-pill ${filter === 'service' ? 'active' : ''}`} onClick={() => setFilter('service')}>Services</button>
        <button className={`adv-filter-pill ${filter === 'event' ? 'active' : ''}`} onClick={() => setFilter('event')}>Events</button>
        <button className={`adv-filter-pill ${filter === 'product' ? 'active' : ''}`} onClick={() => setFilter('product')}>Products</button>
      </div>

      <div className="adv-grid-search">
        <div className="adv-grid-search__field">
          <span>🔍</span>
          <input
            placeholder="Search by title, description, keywords..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {isLoading ? (
        <div className="adv-loading-container" style={{ textAlign: 'center', padding: '40px 0' }}>
          <div className="adv-spinner"></div>
          <p style={{ marginTop: '12px', color: 'rgba(255,255,255,0.6)' }}>Loading the latest offerings...</p>
        </div>
      ) : topOfferings.length === 0 ? (
        <div className="adv-no-matches">
          <span style={{ fontSize: '48px', marginBottom: '16px', display: 'block' }}>🔍</span>
          <h3>No match found</h3>
          <p>We couldn't find any services or events matching your criteria. Try adjusting your search filters!</p>
        </div>
      ) : (
        <>
          <div className="adv-ev-grid">
            {topOfferings.map((ev, index) => {
              const offeringId = ev.offeringId;
              return (
                <div key={offeringId} className="adv-ev-card" onClick={() => onBook(offeringId)}>
                  <div
                    className="adv-ev-card__img"
                    style={ev.coverImageUrl ? { backgroundImage: `url(${ev.coverImageUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' } : { background: getGradient(index) }}
                  >
                    {ev.badge && <span className="adv-ev-card__badge">{ev.badge}</span>}
                    <span className="adv-ev-card__type">
                      {ev.category === 'service' ? 'Service' : ev.category === 'product' ? 'Product' : 'Event'}
                    </span>
                  </div>
                  <div className="adv-ev-card__body">
                    <h3 className="adv-ev-card__name">{ev.title}</h3>
                    <div className="adv-ev-card__meta">
                      <span>{ev.deliveryType?.toLowerCase() === 'virtual' ? 'Virtual' : `In-Person${ev.city ? ` (${ev.city})` : ''}`}</span>
                      <span>{ev.provider?.name || 'Verified Provider'}</span>
                    </div>
                    <div className="adv-ev-card__foot">
                      <span className="adv-ev-card__price">{formatPrice(ev)}</span>
                      <button className="adv-ev-card__btn" onClick={(e) => { e.stopPropagation(); onBook(offeringId); }}>Book Now</button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="adv-view-all-cta" style={{ textAlign: 'center', marginTop: '48px' }}>
            <button
              className="an__cta"
              style={{ padding: '14px 40px', fontSize: '14px', borderRadius: '30px' }}
              onClick={() => navigate('/facilitate/marketplace')}
            >
              Explore All Marketplace Offerings →
            </button>
          </div>
        </>
      )}
    </section>
  );
}

/* ── How It Works ───────────────────────── */
function HowItWorks() {
  const [activeTab, setActiveTab] = useState('customer');

  const customerSteps = [
    { icon: '🔍', step: '01', title: 'Discover & Compare', desc: 'Browse, Buy, Create a new type of economy' },
    { icon: '💬', step: '02', title: 'Book or Negotiate', desc: 'Book it or ask for a custom quote and create smart contracts right on the platform' },
    { icon: '🛡️', step: '03', title: 'Secure Escrow Checkout', desc: 'Pay safely. Payouts are locked securely in Stripe escrow until completion.' }
  ];

  const providerSteps = [
    { icon: '👤', step: '01', title: 'Onboard Stripe Express', desc: 'Register your verified profile and link your bank account directly via Stripe Connect.' },
    { icon: '🛠️', step: '02', title: 'List Your Offerings', desc: 'Publish goods, list local services (hourly/daily), or sell event tickets.' },
    { icon: '📈', step: '03', title: 'Manage Orders & Earnings', desc: 'Process booking requests, resolve disputes, track shipments, and receive secure payouts.' }
  ];

  const currentSteps = activeTab === 'customer' ? customerSteps : providerSteps;

  return (
    <section className="adv-sec adv-how">
      <div className="adv-sec__hdr">
        <h2 className="adv-sec__title">How It <span className="c-green">Works</span></h2>
        <p className="adv-sec__sub">A secure, dual-sided marketplace for both clients and providers</p>
      </div>

      <div className="adv-how-tabs">
        <button
          className={`adv-how-tab ${activeTab === 'customer' ? 'active' : ''}`}
          onClick={() => setActiveTab('customer')}
        >
          For Customers
        </button>
        <button
          className={`adv-how-tab ${activeTab === 'provider' ? 'active' : ''}`}
          onClick={() => setActiveTab('provider')}
        >
          For Providers
        </button>
      </div>

      <div className="adv-how__row">
        {currentSteps.map((s, i) => (
          <React.Fragment key={s.step}>
            <div className="adv-how__step">
              <div className="adv-how__icon">{s.icon}</div>
              <span className="adv-how__num">{s.step}</span>
              <h3 className="adv-how__title">{s.title}</h3>
              <p className="adv-how__desc">{s.desc}</p>
            </div>
            {i < currentSteps.length - 1 && <div className="adv-how__arrow">→</div>}
          </React.Fragment>
        ))}
      </div>
    </section>
  );
}

/* ── Amalgamate Financials (Full Transparency Showcase) ─────────────────── */
const FACILITATION_RECORDS = [
  {
    id: 'FAC-2026-HC-041',
    date: 'Aug 28, 2026',
    department: 'Healthcare',
    icon: '🏥',
    initiative: 'Community Mobile Pediatric Clinic & Medical Supplies',
    city: 'Vancouver',
    country: 'Canada',
    amount: 124500,
    status: 'Disbursed & Audited',
    votes: '18,420 votes'
  },
  {
    id: 'FAC-2026-ENV-098',
    date: 'Aug 25, 2026',
    department: 'Environmental Protection',
    icon: '🌲',
    initiative: 'Urban Canopy & Native Reforestation Program',
    city: 'Austin',
    country: 'USA',
    amount: 86000,
    status: 'Disbursed & Audited',
    votes: '14,110 votes'
  },
  {
    id: 'FAC-2026-FOOD-112',
    date: 'Aug 21, 2026',
    department: 'Food Supply',
    icon: '🌾',
    initiative: 'Neighborhood Vertical Hydroponics & School Nutrition',
    city: 'London',
    country: 'UK',
    amount: 142300,
    status: 'Disbursed & Audited',
    votes: '22,950 votes'
  },
  {
    id: 'FAC-2026-HP-073',
    date: 'Aug 18, 2026',
    department: 'Human Protection',
    icon: '🛡️',
    initiative: 'Clean Water Infrastructure & Emergency Solar Wells',
    city: 'Nairobi',
    country: 'Kenya',
    amount: 95000,
    status: 'Disbursed & Audited',
    votes: '31,040 votes'
  },
  {
    id: 'FAC-2026-HC-056',
    date: 'Aug 14, 2026',
    department: 'Healthcare',
    icon: '🏥',
    initiative: 'Emergency Mental Health First-Response Network',
    city: 'Berlin',
    country: 'Germany',
    amount: 68000,
    status: 'Disbursed & Audited',
    votes: '11,200 votes'
  },
  {
    id: 'FAC-2026-FOOD-134',
    date: 'Aug 10, 2026',
    department: 'Food Supply',
    icon: '🌾',
    initiative: 'Zero-Waste Community Kitchens & Local Farm Network',
    city: 'Austin',
    country: 'USA',
    amount: 54200,
    status: 'Disbursed & Audited',
    votes: '9,880 votes'
  },
  {
    id: 'FAC-2026-ENV-104',
    date: 'Aug 05, 2026',
    department: 'Environmental Protection',
    icon: '🌲',
    initiative: 'Coastal Watershed Protection & Microplastic Filtration',
    city: 'Tokyo',
    country: 'Japan',
    amount: 110000,
    status: 'Disbursed & Audited',
    votes: '17,650 votes'
  },
  {
    id: 'FAC-2026-HP-088',
    date: 'Jul 29, 2026',
    department: 'Human Protection',
    icon: '🛡️',
    initiative: 'Rapid Cold-Weather Transition Housing & Legal Advocacy',
    city: 'Vancouver',
    country: 'Canada',
    amount: 78500,
    status: 'Disbursed & Audited',
    votes: '13,490 votes'
  }
];

const DEPARTMENTS = [
  { id: 'all', label: 'All Departments', icon: '🌐' },
  { id: 'Healthcare', label: 'Healthcare', icon: '🏥' },
  { id: 'Environmental Protection', label: 'Environmental Protection', icon: '🌲' },
  { id: 'Human Protection', label: 'Human Protection', icon: '🛡️' },
  { id: 'Food Supply', label: 'Food Supply', icon: '🌾' },
];

function PlatformPowerhouse() {
  const [selectedDept, setSelectedDept] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const locations = [
    { value: 'all', label: 'All Cities & Countries' },
    { value: 'Vancouver, Canada', label: 'Vancouver, Canada' },
    { value: 'Austin, USA', label: 'Austin, USA' },
    { value: 'London, UK', label: 'London, UK' },
    { value: 'Nairobi, Kenya', label: 'Nairobi, Kenya' },
    { value: 'Berlin, Germany', label: 'Berlin, Germany' },
    { value: 'Tokyo, Japan', label: 'Tokyo, Japan' },
  ];

  const filteredRecords = FACILITATION_RECORDS.filter(record => {
    if (selectedDept !== 'all' && record.department !== selectedDept) {
      return false;
    }
    if (selectedLocation !== 'all') {
      const locString = `${record.city}, ${record.country}`;
      if (locString !== selectedLocation) return false;
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      const match =
        record.initiative.toLowerCase().includes(q) ||
        record.city.toLowerCase().includes(q) ||
        record.country.toLowerCase().includes(q) ||
        record.department.toLowerCase().includes(q) ||
        record.id.toLowerCase().includes(q);
      if (!match) return false;
    }
    return true;
  });

  const totalFilteredAmount = filteredRecords.reduce((sum, r) => sum + r.amount, 0);

  // return (
  //   <section className="adv-sec adv-powerhouse">
  //     <div className="adv-sec__hdr">
  //       <h2 className="adv-sec__title">Full <span className="c-green">Transparency</span></h2>
  //       <p className="adv-sec__sub">Don't we all wish our governments provided complete transparency. With Amalgamate you can see the flow of every dollar</p>
  //     </div>

  //     {/* ── 4 Key Financial Metrics Cards ── */}
  //     <div className="ph-financials-metrics">
  //       <div className="ph-metric-card ph-metric-card--funds">
  //         <div className="ph-metric-card__top">
  //           <span className="ph-metric-card__icon">💰</span>
  //           <span className="ph-metric-badge badge--green">Live Inflow</span>
  //         </div>
  //         <span className="ph-metric-lbl">Funds Raised</span>
  //         <span className="ph-metric-val">$4,850,200.00</span>
  //         <span className="ph-metric-sub">Generated from voluntary transaction fees</span>
  //       </div>

  //       <div className="ph-metric-card ph-metric-card--costs">
  //         <div className="ph-metric-card__top">
  //           <span className="ph-metric-card__icon">⚙️</span>
  //           <span className="ph-metric-badge badge--amber">5.0% TOE</span>
  //         </div>
  //         <span className="ph-metric-lbl">Total Operating Expenses</span>
  //         <span className="ph-metric-val">$242,510.00</span>
  //         <span className="ph-metric-sub">Strict operational & infrastructure overhead</span>
  //       </div>

  //       <div className="ph-metric-card ph-metric-card--facilitations">
  //         <div className="ph-metric-card__top">
  //           <span className="ph-metric-card__icon">🤝</span>
  //           <span className="ph-metric-badge badge--teal">75.9% Impact</span>
  //         </div>
  //         <span className="ph-metric-lbl">Facilitations</span>
  //         <span className="ph-metric-val">$3,680,450.00</span>
  //         <span className="ph-metric-sub">Disbursed directly to voted community causes</span>
  //       </div>

  //       <div className="ph-metric-card ph-metric-card--resources">
  //         <div className="ph-metric-card__top">
  //           <span className="ph-metric-card__icon">🏦</span>
  //           <span className="ph-metric-badge badge--blue">Treasury</span>
  //         </div>
  //         <span className="ph-metric-lbl">Available Resources</span>
  //         <span className="ph-metric-val">$927,240.00</span>
  //         <span className="ph-metric-sub">In user voting accounts & active reserves</span>
  //       </div>
  //     </div>

  //     {/* ── Searchable Facilitations Accounting Database ── */}
  //     <div className="ph-database-box glass-card">
  //       <div className="ph-db-header">
  //         <div>
  //           <div className="ph-db-title-row">
  //             <h3>Facilitations Accounting Database</h3>
  //             <span className="ph-db-verified-badge">● 100% Public Ledger</span>
  //           </div>
  //           <p className="ph-db-subtitle">
  //             Searchable real-world disbursements by city, country, and community department.
  //           </p>
  //         </div>
  //         <div className="ph-db-stats-pill">
  //           <span>Showing <strong>{filteredRecords.length}</strong> of {FACILITATION_RECORDS.length} Facilitations</span>
  //         </div>
  //       </div>

  //       {/* ── Search & Filter Controls ── */}
  //       <div className="ph-db-controls">
  //         <div className="ph-db-search">
  //           <span className="ph-db-search-icon">🔍</span>
  //           <input
  //             type="text"
  //             placeholder="Search by city, country, initiative, or ledger ID..."
  //             value={searchQuery}
  //             onChange={(e) => setSearchQuery(e.target.value)}
  //           />
  //           {searchQuery && (
  //             <button className="ph-db-clear-btn" onClick={() => setSearchQuery('')}>✕</button>
  //           )}
  //         </div>

  //         <div className="ph-db-dropdown-wrap">
  //           <span className="ph-db-pin">📍</span>
  //           <select
  //             value={selectedLocation}
  //             onChange={(e) => setSelectedLocation(e.target.value)}
  //             className="ph-db-select"
  //           >
  //             {locations.map(loc => (
  //               <option key={loc.value} value={loc.value}>{loc.label}</option>
  //             ))}
  //           </select>
  //         </div>
  //       </div>

  //       {/* ── Department Pills ── */}
  //       <div className="ph-dept-pills">
  //         {DEPARTMENTS.map(d => (
  //           <button
  //             key={d.id}
  //             className={`ph-dept-pill ${selectedDept === d.id ? 'active' : ''}`}
  //             onClick={() => setSelectedDept(d.id)}
  //           >
  //             <span className="ph-dept-icon">{d.icon}</span>
  //             <span>{d.label}</span>
  //           </button>
  //         ))}
  //       </div>

  //       {/* ── Ledger Entries Table / List ── */}
  //       <div className="ph-ledger-list">
  //         {filteredRecords.length === 0 ? (
  //           <div className="ph-ledger-empty">
  //             <span style={{ fontSize: '36px' }}>🔎</span>
  //             <h4>No facilitations match your search criteria</h4>
  //             <p>Try clearing your filters or selecting a different department / location.</p>
  //             <button
  //               className="ph-reset-filters-btn"
  //               onClick={() => { setSelectedDept('all'); setSelectedLocation('all'); setSearchQuery(''); }}
  //             >
  //               Reset All Filters
  //             </button>
  //           </div>
  //         ) : (
  //           filteredRecords.map(item => (
  //             <div key={item.id} className="ph-ledger-row">
  //               <div className="ph-row-main">
  //                 <div className="ph-row-icon-wrap">
  //                   <span className="ph-row-icon">{item.icon}</span>
  //                 </div>
  //                 <div className="ph-row-info">
  //                   <div className="ph-row-top">
  //                     <span className={`ph-dept-badge dept-${item.department.toLowerCase().replace(/\s+/g, '-')}`}>
  //                       {item.department}
  //                     </span>
  //                     <span className="ph-row-location">📍 {item.city}, {item.country}</span>
  //                     <span className="ph-row-id">{item.id}</span>
  //                   </div>
  //                   <h4 className="ph-row-initiative">{item.initiative}</h4>
  //                   <div className="ph-row-meta">
  //                     <span className="ph-row-date">🗓️ {item.date}</span>
  //                     <span className="ph-row-votes">🗳️ {item.votes}</span>
  //                     <span className="ph-row-audit">✔️ {item.status}</span>
  //                   </div>
  //                 </div>
  //               </div>

  //               <div className="ph-row-financials">
  //                 <span className="ph-row-amount">+${item.amount.toLocaleString()}.00</span>
  //                 <span className="ph-row-badge-disbursed">Disbursed</span>
  //               </div>
  //             </div>
  //           ))
  //         )}
  //       </div>

  //       {/* ── Database Footer Summary ── */}
  //       <div className="ph-db-footer">
  //         <div className="ph-db-footer-left">
  //           <span className="ph-audit-shield">🛡️</span>
  //           <span>All Amalgamate transactions and voter-directed facilitations are cryptographically validated and open for public audit.</span>
  //         </div>
  //         <div className="ph-db-footer-right">
  //           <span className="ph-db-subtotal-lbl">Filtered Total:</span>
  //           <span className="ph-db-subtotal-val">${totalFilteredAmount.toLocaleString()}.00</span>
  //         </div>
  //       </div>
  //     </div>
  //   </section>
  // );
}



/* ── Facilitate Core Pillars ──────────────── */
function FacilitatePillars({ navigate }) {
  const pillars = [
    {
      title: 'Buy and sell marketplace',
      desc: 'Our goal is to provide a platform that can transact nearly anything',
      link: '/facilitate/marketplace',
      btnText: 'Shop Marketplace'
    },
    {
      title: 'Offer and Hire Services',
      desc: 'Estimate, and facilitate any service you can imagine.',
      link: '/facilitate/marketplace',
      btnText: 'Book Services'
    },
    {
      title: 'Events & Ticketing',
      desc: 'Create, register for, and purchase tickets to public or private gatherings.',
      link: '/facilitate/marketplace',
      btnText: 'Browse Events'
    },
    {
      title: 'Secure Payouts',
      desc: 'Onboard Stripe Express, receive escrowed earnings, and transfer to your bank.',
      link: '/facilitate/settings/payouts',
      btnText: 'Payout Settings'
    },
    {
      title: 'Order Management',
      desc: 'Ship, track, dispatch, validate and facilitate',
      link: '/facilitate/orders',
      btnText: 'Manage Orders'
    },
    {
      title: 'Donate to Facilitate',
      desc: 'Donate to help us grow faster and drive more change',
      link: '/amalgamate',
      btnText: 'Donate & Support'
    }
  ];

  return (
    <section className="adv-sec fac-pillars">
      <div className="adv-sec__hdr">
        <span className="sec-step-badge">CORE PILLARS · ACTION & COMMERCE</span>
        <h2 className="adv-sec__title">The Pillars of <span className="c-green">Facilitate</span></h2>
        <p className="adv-sec__sub">Providing the tools, opportunities, and transactions that help people accomplish change</p>
      </div>

      <div className="fac-pillars__grid">
        {pillars.map((p) => (
          <div key={p.title} className="fac-pillar-card glass-card">
            <h3 className="fac-pillar-card__title">{p.title}</h3>
            <p className="fac-pillar-card__desc">{p.desc}</p>
            <button className="fac-pillar-card__btn" onClick={() => navigate(p.link)}>
              {p.btnText} <span>→</span>
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ── Organizers ─────────────────────────── */
function Organizers() {
  const navigate = useNavigate();

  const handleListOfferingClick = () => {
    if (getToken() && getUserId()) {
      navigate('/facilitate/offerings/create');
    } else {
      localStorage.setItem("authRedirectPath", '/facilitate/offerings/create');
      navigate("/login");
    }
  };

  return (
    <section className="adv-org">
      <div className="adv-org__inner">
        <div className="adv-org__left">
          <span className="adv-org__eyebrow">FOR PROVIDERS</span>
          <h2 className="adv-org__title">Host events or services?<br /><span className="c-green">Grow with Facilitate</span></h2>
          <p className="adv-org__sub">Join providers who trust Facilitate to list services, sell event tickets, manage customer quotes, and scale their business.</p>
          <div className="adv-org__cta">
            <button className="adv-org__btn-primary" onClick={handleListOfferingClick}>List Your Offering →</button>
          </div>
        </div>
        <div className="adv-org__benefits">
          {ORG_BENEFITS.map(b => (
            <div key={b.title} className="adv-org__benefit">
              <h4>{b.title}</h4>
              <p>{b.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── App Banner ─────────────────────────── */
function AppBanner() {
  return (
    <section className="adv-app">
      <div className="adv-app__inner">
        <div className="adv-app__text">
          <h2>Take Facilitate <span className="c-green">everywhere</span></h2>
          <p>Download the app and never miss an event. Exclusive in-app deals, real-time notifications, and easy mobile check-in.</p>
          <div className="adv-app__badges">
            {[{ icon: '▶', sm: 'Get it on', lg: 'Google Play' }, { icon: '', sm: 'Download on the', lg: 'App Store' }].map(b => (
              <button key={b.lg} className="adv-app__badge">
                <span className="adv-app__badge-icon">{b.icon}</span>
                <div><span className="adv-app__badge-sm">{b.sm}</span><span className="adv-app__badge-lg">{b.lg}</span></div>
              </button>
            ))}
          </div>
        </div>
        <div className="adv-app__phone">
          <div className="adv-app__phone-notch" />
          <div className="adv-app__phone-bar" />
          <div className="adv-app__phone-card" />
          <div className="adv-app__phone-card adv-app__phone-card--sm" />
          <div className="adv-app__phone-dots"><span /><span /><span className="active" /></div>
        </div>
      </div>
    </section>
  );
}

/* ── Page root ──────────────────────────── */
export default function AdventurePage() {
  const navigate = useNavigate();
  const [offerings, setOfferings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchOfferings = async () => {
      setIsLoading(true);
      try {
        const res = await getOfferings({ status: 'active' });
        setOfferings(res.data || []);
      } catch (err) {
        console.error("Failed to fetch offerings in AdventurePage:", err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchOfferings();
  }, []);

  const handleBookingClick = (offeringId) => {
    // If it is a mock offering (which has numeric id), just redirect to login for demo purposes
    if (typeof offeringId === 'number' || offeringId.startsWith('mock-')) {
      if (getToken() && getUserId()) {
        navigate('/facilitate/marketplace');
      } else {
        localStorage.setItem("authRedirectPath", '/facilitate/marketplace');
        navigate("/login");
      }
      return;
    }

    if (getToken() && getUserId()) {
      navigate(`/facilitate/offerings/${offeringId}`);
    } else {
      localStorage.setItem("authRedirectPath", `/facilitate/offerings/${offeringId}`);
      navigate("/login");
    }
  };

  const displayOfferings = offerings || [];

  // Apply Search form filters
  const filteredOfferings = displayOfferings.filter(o => {
    // Search query filter (title/description/venue)
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const titleMatch = o.title?.toLowerCase().includes(q) || false;
      const descMatch = o.description?.toLowerCase().includes(q) || false;
      const venueMatch = o.venue?.toLowerCase().includes(q) || false;
      if (!titleMatch && !descMatch && !venueMatch) return false;
    }

    return true;
  });

  return (
    <>
      <SEO
        title="Facilitate - Transactions that empower Change | Amalgamate"
        description="Amalgamate is designed to facilitate the real world changes that people want. Facilitate transactions for you and your business and empower change."
        image="/Assets/AmalgamateHUb/facilitate_bg.jpg"
        slugPath="/facilitate"
      />
      <div className="adv-page">
        <Navbar navigate={navigate} />
        <Hero />
        <FacilitatePillars navigate={navigate} />
        <Featured
          offerings={filteredOfferings}
          isLoading={isLoading}
          onBook={handleBookingClick}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          navigate={navigate}
        />
        <HowItWorks />
        <Organizers />
        <PlatformPowerhouse />
        <Footer />
      </div>
    </>
  );
}
